import { useState, useEffect } from 'react';

const SELECTED_DEPLOYMENT_STORAGE_KEY = 'activeDeploymentId';

/**
 * Custom hook for managing deployment selection and edit states
 * @param {Array} deployments - Array of deployments
 * @param {Array} linkedFamilies - Array of linked families
 * @param {Array} releases - Array of releases
 * @returns {Object} selected deployment, edit states, and handlers
 */
export function useDeploymentSelection(deployments, linkedFamilies, releases) {
    // Selection state
    const [selectedDeployment, setSelectedDeployment] = useState(null);
    const [detailTab, setDetailTab] = useState('embed'); // 'embed' | 'builder' | 'analytics'

    // Edit states for selected deployment
    const [editName, setEditName] = useState('');
    const [editGuiVersionTag, setEditGuiVersionTag] = useState('1.0.0');
    const [allowedOrigins, setAllowedOrigins] = useState('');
    const [editReleaseId, setEditReleaseId] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [isActive, setIsActive] = useState(true);

    // Copy feedback states
    const [copiedToken, setCopiedToken] = useState(false);
    const [copiedSnippet, setCopiedSnippet] = useState(false);
    const [copiedWidget, setCopiedWidget] = useState(false);
    const [activeEmbedTab, setActiveEmbedTab] = useState('iframe');

    // Configure deployment for editing
    const handleConfigureDeployment = (dep, nextDetailTab = 'embed') => {
        setSelectedDeployment(dep);
        setEditName(dep.name);
        setEditGuiVersionTag(dep.guiVersionTag);
        setEditReleaseId(dep.releaseId || '');
        setAllowedOrigins(dep.allowedOrigins || '');
        setExpiresAt(dep.expiresAt ? dep.expiresAt.split('T')[0] : '');
        setIsActive(dep.isActive);
        setDetailTab(nextDetailTab);
    };

    // Restore and refresh selection when deployments change
    useEffect(() => {
        if (deployments.length === 0) {
            setSelectedDeployment(null);
            return;
        }

        if (!selectedDeployment) {
            const storedDeploymentId = localStorage.getItem(SELECTED_DEPLOYMENT_STORAGE_KEY);
            const restoredDeployment = deployments.find(d => String(d.id) === String(storedDeploymentId));
            if (restoredDeployment) {
                setSelectedDeployment(restoredDeployment);
            }
            return;
        }
        
        const refreshedDeployment = deployments.find(d => d.id === selectedDeployment.id);
        if (refreshedDeployment) {
            setSelectedDeployment(refreshedDeployment);
        } else {
            setSelectedDeployment(null);
        }
    }, [deployments, selectedDeployment]);

    useEffect(() => {
        if (selectedDeployment?.id) {
            localStorage.setItem(SELECTED_DEPLOYMENT_STORAGE_KEY, selectedDeployment.id);
        } else {
            localStorage.removeItem(SELECTED_DEPLOYMENT_STORAGE_KEY);
        }
    }, [selectedDeployment]);

    // Reset edit states when selection changes
    useEffect(() => {
        if (selectedDeployment) {
            setEditName(selectedDeployment.name);
            setEditGuiVersionTag(selectedDeployment.guiVersionTag);
            setEditReleaseId(selectedDeployment.releaseId || '');
            setAllowedOrigins(selectedDeployment.allowedOrigins || '');
            setExpiresAt(selectedDeployment.expiresAt ? selectedDeployment.expiresAt.split('T')[0] : '');
            setIsActive(selectedDeployment.isActive);
        } else {
            setEditName('');
            setEditGuiVersionTag('1.0.0');
            setAllowedOrigins('');
            setEditReleaseId('');
            setExpiresAt('');
            setIsActive(true);
        }
    }, [selectedDeployment]);

    return {
        selectedDeployment,
        setSelectedDeployment,
        detailTab,
        setDetailTab,
        editName,
        setEditName,
        editGuiVersionTag,
        setEditGuiVersionTag,
        allowedOrigins,
        setAllowedOrigins,
        editReleaseId,
        setEditReleaseId,
        expiresAt,
        setExpiresAt,
        isActive,
        setIsActive,
        copiedToken,
        setCopiedToken,
        copiedSnippet,
        setCopiedSnippet,
        copiedWidget,
        setCopiedWidget,
        activeEmbedTab,
        setActiveEmbedTab,
        handleConfigureDeployment
    };
}
